import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function Home() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isPro, setIsPro] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (!u) { navigate('/'); return; }
    const parsed = JSON.parse(u);
    setUser(parsed);

    // Check subscription status
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/api/stripe/status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      setIsPro(r.data.isPro);
      if (r.data.isPro) {
        const updated = { ...parsed, subscriptionTier: 'pro' };
        localStorage.setItem('user', JSON.stringify(updated));
      }
    }).catch(() => {});

    // Check if coming back from successful upgrade
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      setIsPro(true);
      window.history.replaceState({}, '', '/home');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/stripe/create-checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkoutUrl;
    } catch {
      alert('Could not start checkout. Please try again.');
      setUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/stripe/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.portalUrl;
    } catch {
      alert('Could not open billing portal. Please try again.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.brand}>VIRILITER</p>
        <h1 style={styles.title}>My Ready Room</h1>
        <p style={styles.tagline}>Train for the moment that defines your career.</p>

        <div style={styles.welcomeCard}>
          <div style={styles.welcomeTop}>
            <div>
              <p style={styles.welcomeLabel}>WELCOME BACK</p>
              <p style={styles.welcomeName}>{user?.fullName || 'Executive'}</p>
            </div>
            <div style={{
              ...styles.tierBadge,
              backgroundColor: isPro ? 'rgba(201,169,110,0.1)' : 'rgba(59,130,246,0.1)',
              borderColor: isPro ? '#C9A96E' : '#3B82F6'
            }}>
              <span style={{ color: isPro ? '#C9A96E' : '#3B82F6', fontSize: '10px', fontWeight: '700', letterSpacing: '2px' }}>
                {isPro ? 'PRO' : 'FREE'}
              </span>
            </div>
          </div>
          <p style={styles.welcomeSub}>Ready to sharpen your edge?</p>
        </div>

        <div style={styles.actions}>
          <button style={styles.primaryButton} onClick={() => navigate('/setup')}>
            <span>Start Interview Prep</span>
            <span>→</span>
          </button>
          <button style={styles.progressButton} onClick={() => navigate('/presentation-setup')}>
            <span>Presentation Prep</span>
            <span>→</span>
          </button>
          <button style={styles.progressButton} onClick={() => navigate('/history')}>
            <span>My Progress</span>
            <span>→</span>
          </button>
        </div>

        {!isPro && (
          <div style={styles.upgradeCard}>
            <div style={styles.upgradeLeft}>
              <p style={styles.upgradeLabel}>UPGRADE TO PRO</p>
              <p style={styles.upgradeText}>Unlimited sessions, Presentation Prep, file upload, auto-redact and more.</p>
            </div>
            <button style={styles.upgradeButton} onClick={handleUpgrade} disabled={upgrading}>
              {upgrading ? '...' : '$19/mo →'}
            </button>
          </div>
        )}

        {isPro && (
          <button style={styles.billingButton} onClick={handleManageBilling}>
            Manage Billing
          </button>
        )}

        <button style={styles.signOut} onClick={handleLogout}>Sign Out</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  card: { width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '16px' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  title: { color: '#F1F5F9', fontSize: '34px', fontWeight: '800', textAlign: 'center', margin: 0, letterSpacing: '-0.5px' },
  tagline: { color: '#475569', fontSize: '13px', textAlign: 'center', margin: 0 },
  welcomeCard: { backgroundColor: '#0F172A', borderRadius: '16px', padding: '24px', border: '1px solid #1E293B' },
  welcomeTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  welcomeLabel: { color: '#3B82F6', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: '0 0 6px 0' },
  welcomeName: { color: '#F1F5F9', fontSize: '24px', fontWeight: '700', margin: 0 },
  tierBadge: { border: '1px solid', borderRadius: '6px', padding: '4px 10px' },
  welcomeSub: { color: '#475569', fontSize: '14px', margin: 0 },
  actions: { display: 'flex', flexDirection: 'column', gap: '10px' },
  primaryButton: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '20px', borderRadius: '12px', fontSize: '17px', fontWeight: '700', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  progressButton: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '18px', borderRadius: '12px', fontSize: '16px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  upgradeCard: { backgroundColor: '#0A1628', borderRadius: '12px', padding: '18px 20px', border: '1px solid #1E3A5F', display: 'flex', alignItems: 'center', gap: '16px' },
  upgradeLeft: { flex: 1 },
  upgradeLabel: { color: '#C9A96E', fontSize: '10px', letterSpacing: '3px', fontWeight: '700', margin: '0 0 4px 0' },
  upgradeText: { color: '#475569', fontSize: '12px', lineHeight: '1.5', margin: 0 },
  upgradeButton: { backgroundColor: '#C9A96E', color: '#13120F', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' },
  billingButton: { backgroundColor: 'transparent', color: '#475569', padding: '12px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer' },
  signOut: { backgroundColor: 'transparent', color: '#334155', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '8px' },
};